/**
 * Shared helpers for the storage-layout trees (Content Management, Archive,
 * Copy/Move destination picker). Items carry `path_segments` mirroring the
 * physical storage format — Section / Sub-Section / File Category / Year /
 * File Start Date — and sit on the deepest (date) node, exactly like on disk.
 */

export interface StoragePathNode<TItem> {
  children: Record<string, StoragePathNode<TItem>>;
  items: TItem[];
}

export function buildStoragePathTree<TItem extends { path_segments?: string[] }>(
  rows: TItem[]
): StoragePathNode<TItem> {
  const root: StoragePathNode<TItem> = { children: {}, items: [] };
  for (const row of rows) {
    let node = root;
    for (const seg of row.path_segments ?? []) {
      if (!node.children[seg]) node.children[seg] = { children: {}, items: [] };
      node = node.children[seg];
    }
    node.items.push(row);
  }
  return root;
}

/** DD-MM-YYYY date folders sort chronologically; everything else A→Z. */
export function comparePathLabels(a: string, b: string): number {
  const dateRe = /^(\d{2})-(\d{2})-(\d{4})$/;
  const ma = a.match(dateRe);
  const mb = b.match(dateRe);
  if (ma && mb) {
    return `${ma[3]}${ma[2]}${ma[1]}`.localeCompare(`${mb[3]}${mb[2]}${mb[1]}`);
  }
  return a.localeCompare(b);
}
